import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import '../../styles/admin/AdminJobForm.css';

const AdminJobEdit = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    summary: '',
    description: '',
    requirements: '',
    benefits: '',
    image: '',
    department: 'engineering',
    job_type: 'full-time',
    experience_level: 'mid',
    min_salary: '',
    max_salary: '',
    location: 'Ho Chi Minh City',
    remote_allowed: false,
    status: 'active',
    featured: false,
    positions_available: 1,
    application_deadline: '',
    contact_email: '',
    contact_phone: ''
  });

  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [uploading, setUploading] = useState(false);

  const API_BASE_URL = "https://phucansolutions.vn" || '"https://phucansolutions.vn"';

  const departments = [
    { value: 'engineering', label: 'Kỹ thuật' },
    { value: 'marketing', label: 'Marketing' },
    { value: 'sales', label: 'Kinh doanh' },
    { value: 'hr', label: 'Nhân sự' },
    { value: 'finance', label: 'Tài chính' },
    { value: 'operations', label: 'Vận hành' },
    { value: 'design', label: 'Thiết kế' },
    { value: 'product', label: 'Sản phẩm' }
  ];

  const jobTypes = [
    { value: 'full-time', label: 'Toàn thời gian' },
    { value: 'part-time', label: 'Bán thời gian' },
    { value: 'contract', label: 'Hợp đồng' },
    { value: 'internship', label: 'Thực tập' },
    { value: 'freelance', label: 'Freelance' }
  ];

  const experienceLevels = [
    { value: 'entry', label: 'Mới ra trường' },
    { value: 'junior', label: 'Junior (1-2 năm)' },
    { value: 'mid', label: 'Middle (3-5 năm)' },
    { value: 'senior', label: 'Senior (5+ năm)' },
    { value: 'lead', label: 'Team Lead' },
    { value: 'manager', label: 'Manager' }
  ];

  const statuses = [
    { value: 'active', label: 'Đang tuyển' },
    { value: 'paused', label: 'Tạm dừng' },
    { value: 'filled', label: 'Đã tuyển đủ' },
    { value: 'expired', label: 'Hết hạn' }
  ];

  const locations = [
    'Ho Chi Minh City',
    'Hanoi',
    'Da Nang',
    'Can Tho',
    'Hai Phong',
    'Nha Trang',
    'Hue',
    'Vung Tau'
  ];

  // Load existing job data
  useEffect(() => {
    fetchJobData();
  }, [id]);

  const fetchJobData = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_BASE_URL}/api/admin/jobs/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (response.ok && data.success) {
        const jobData = data.data;
        
        setFormData({
          title: jobData.title || '',
          slug: jobData.slug || '',
          summary: jobData.summary || '',
          description: jobData.description || '',
          requirements: jobData.requirements || '',
          benefits: jobData.benefits || '',
          image: jobData.image || '',
          department: jobData.department || 'engineering',
          job_type: jobData.job_type || 'full-time',
          experience_level: jobData.experience_level || 'mid',
          min_salary: jobData.min_salary || '',
          max_salary: jobData.max_salary || '',
          location: jobData.location || 'Ho Chi Minh City',
          remote_allowed: Boolean(jobData.remote_allowed),
          status: jobData.status || 'active',
          featured: jobData.featured || false,
          positions_available: jobData.positions_available || 1,
          application_deadline: jobData.application_deadline ? 
            jobData.application_deadline.split('T')[0] : '',
          contact_email: jobData.contact_email || '',
          contact_phone: jobData.contact_phone || ''
        });

        // Set existing image preview if available
        if (jobData.image) {
          setImagePreview(`${API_BASE_URL}${jobData.image}`);
        }

      } else {
        alert('Không thể tải thông tin tin tuyển dụng: ' + data.error);
        navigate('/admin/jobs');
      }
    } catch (error) {
      console.error('Error fetching job:', error);
      alert('Lỗi kết nối server');
      navigate('/admin/jobs');
    } finally {
      setInitialLoading(false);
    }
  };

  // Auto generate slug from title
  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[đĐ]/g, 'd')
      .replace(/[^a-z0-9\s-]/g, '')
      .trim()
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-');
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    let newValue = type === 'checkbox' ? checked : value;

    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));

    // Auto-generate slug when title changes
    if (name === 'title') {
      const newSlug = generateSlug(value);
      setFormData(prev => ({
        ...prev,
        slug: newSlug
      }));
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadImage = async () => {
    if (!imageFile) return null;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('image', imageFile);

      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_BASE_URL}/api/admin/jobs/upload-image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json();
      if (data.success) {
        return data.data.imageUrl;
      } else {
        throw new Error(data.error || 'Lỗi upload hình ảnh');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Lỗi upload hình ảnh: ' + error.message);
      return null;
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.title || !formData.description) {
      alert('Vui lòng điền đầy đủ tiêu đề và mô tả công việc');
      return;
    }

    setLoading(true);

    try {
      // Upload image first if a new image was selected
      let imageUrl = formData.image;
      if (imageFile) {
        imageUrl = await uploadImage();
        if (imageUrl === null) {
          setLoading(false);
          return; // Upload failed
        }
      }

      const token = localStorage.getItem('admin_token');
      
      // Prepare data for submission
      const submitData = {
        ...formData,
        image: imageUrl,
        min_salary: formData.min_salary ? parseInt(formData.min_salary) : null,
        max_salary: formData.max_salary ? parseInt(formData.max_salary) : null,
        positions_available: parseInt(formData.positions_available) || 1
      };

      const response = await fetch(`${API_BASE_URL}/api/admin/jobs/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(submitData)
      });

      const data = await response.json();

      if (response.ok && data.success) {
        alert('Cập nhật tin tuyển dụng thành công!');
        navigate('/admin/jobs');
      } else {
        alert('Lỗi: ' + (data.error || 'Không thể cập nhật tin tuyển dụng'));
      }
    } catch (error) {
      console.error('Error updating job:', error);
      alert('Lỗi kết nối server');
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <div className="admin-job-form">
        <div className="loading-state">
          <div className="loading-spinner">🔄</div>
          <p>Đang tải thông tin tin tuyển dụng...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-job-form">
      <div className="form-header">
        <div className="header-content">
          <h1>✏️ Chỉnh Sửa Tin Tuyển Dụng</h1>
          <p>Cập nhật thông tin tin tuyển dụng #{id}</p>
        </div>
        <div className="header-actions">
          <button 
            type="button" 
            onClick={() => navigate('/admin/jobs')}
            className="cancel-btn"
          >
            ← Quay lại
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="job-form">
        <div className="form-grid">
          {/* Left Column */}
          <div className="form-column">
            {/* Basic Information */}
            <div className="form-section">
              <h3>📝 Thông tin cơ bản</h3>
              
              <div className="form-group">
                <label htmlFor="title">Tiêu đề vị trí *</label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="Ví dụ: Senior Frontend Developer"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="slug">URL Slug</label>
                <input
                  type="text"
                  id="slug"
                  name="slug"
                  value={formData.slug}
                  onChange={handleInputChange}
                  placeholder="url-slug-tu-dong-tao"
                />
                <small>URL hiển thị trên website. Để trống để tự động tạo từ tiêu đề</small>
              </div>

              <div className="form-group">
                <label htmlFor="summary">Tóm tắt công việc</label>
                <textarea
                  id="summary"
                  name="summary"
                  value={formData.summary}
                  onChange={handleInputChange}
                  placeholder="Mô tả ngắn gọn về vị trí tuyển dụng..."
                  rows="3"
                />
                <small>Mô tả ngắn hiển thị trong danh sách tuyển dụng</small>
              </div>

              <div className="form-group">
                <label htmlFor="description">Mô tả công việc *</label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Mô tả chi tiết về công việc, trách nhiệm..."
                  rows="6"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="requirements">Yêu cầu ứng viên</label>
                <textarea
                  id="requirements"
                  name="requirements"
                  value={formData.requirements}
                  onChange={handleInputChange}
                  placeholder="Liệt kê các yêu cầu về kinh nghiệm, kỹ năng..."
                  rows="5"
                />
              </div>

              <div className="form-group">
                <label htmlFor="benefits">Quyền lợi</label>
                <textarea
                  id="benefits"
                  name="benefits"
                  value={formData.benefits}
                  onChange={handleInputChange}
                  placeholder="Mô tả các quyền lợi, phúc lợi của công ty..."
                  rows="4"
                />
              </div>

              {/* Image Upload Section */}
              <div className="form-group">
                <label htmlFor="image">Hình ảnh minh họa</label>
                <div className="image-upload-container">
                  {imagePreview && (
                    <div className="image-preview">
                      <img src={imagePreview} alt="Preview" className="preview-image" />
                      <button 
                        type="button" 
                        onClick={() => {
                          setImageFile(null);
                          setImagePreview('');
                          setFormData(prev => ({ ...prev, image: '' }));
                        }}
                        className="remove-image-btn"
                      >
                        ✕
                      </button>
                    </div>
                  )}
                  <div className="file-input-wrapper">
                    <input
                      type="file"
                      id="image"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="file-input"
                    />
                    <label htmlFor="image" className="file-input-label">
                      📷 {imageFile ? 'Đổi hình ảnh' : (imagePreview ? 'Đổi hình ảnh' : 'Chọn hình ảnh')}
                    </label>
                  </div>
                  <small>Chấp nhận: JPG, PNG, GIF, WebP. Tối đa 5MB</small>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className="form-column">
            {/* Job Details */}
            <div className="form-section">
              <h3>🏢 Thông tin vị trí</h3>
              
              <div className="form-group">
                <label htmlFor="department">Phòng ban</label>
                <select
                  id="department"
                  name="department"
                  value={formData.department}
                  onChange={handleInputChange}
                >
                  {departments.map(dept => (
                    <option key={dept.value} value={dept.value}>
                      {dept.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="job_type">Loại công việc</label>
                <select
                  id="job_type"
                  name="job_type"
                  value={formData.job_type}
                  onChange={handleInputChange}
                >
                  {jobTypes.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="experience_level">Cấp độ kinh nghiệm</label>
                <select
                  id="experience_level"
                  name="experience_level"
                  value={formData.experience_level}
                  onChange={handleInputChange}
                >
                  {experienceLevels.map(level => (
                    <option key={level.value} value={level.value}>
                      {level.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="positions_available">Số lượng tuyển</label>
                <input
                  type="number"
                  id="positions_available"
                  name="positions_available"
                  value={formData.positions_available}
                  onChange={handleInputChange}
                  min="1"
                  max="100"
                />
              </div>
            </div>

            {/* Salary & Location */}
            <div className="form-section">
              <h3>💰 Lương & Địa điểm</h3>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="min_salary">Lương tối thiểu (VND)</label>
                  <input
                    type="number"
                    id="min_salary"
                    name="min_salary"
                    value={formData.min_salary}
                    onChange={handleInputChange}
                    placeholder="Ví dụ: 10000000"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="max_salary">Lương tối đa (VND)</label>
                  <input
                    type="number"
                    id="max_salary"
                    name="max_salary"
                    value={formData.max_salary}
                    onChange={handleInputChange}
                    placeholder="Ví dụ: 20000000"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="location">Địa điểm làm việc</label>
                <select
                  id="location"
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                >
                  {locations.map(location => (
                    <option key={location} value={location}>
                      {location}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="remote_allowed"
                    checked={formData.remote_allowed}
                    onChange={handleInputChange}
                  />
                  <span className="checkmark"></span>
                  <span className="label-text">🏠 Cho phép làm việc từ xa</span>
                </label>
              </div>
            </div>

            {/* Contact & Settings */}
            <div className="form-section">
              <h3>📞 Liên hệ & Cài đặt</h3>
              
              <div className="form-group">
                <label htmlFor="contact_email">Email liên hệ</label>
                <input
                  type="email"
                  id="contact_email"
                  name="contact_email"
                  value={formData.contact_email}
                  onChange={handleInputChange}
                  placeholder="hr@company.com"
                />
              </div>

              <div className="form-group">
                <label htmlFor="contact_phone">Số điện thoại</label>
                <input
                  type="tel"
                  id="contact_phone"
                  name="contact_phone"
                  value={formData.contact_phone}
                  onChange={handleInputChange}
                  placeholder="+84 123 456 789"
                />
              </div>

              <div className="form-group">
                <label htmlFor="application_deadline">Hạn nộp hồ sơ</label>
                <input
                  type="date"
                  id="application_deadline"
                  name="application_deadline"
                  value={formData.application_deadline}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="status">Trạng thái</label>
                <select
                  id="status"
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                >
                  {statuses.map(status => (
                    <option key={status.value} value={status.value}>
                      {status.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="featured"
                    checked={formData.featured}
                    onChange={handleInputChange}
                  />
                  <span className="checkmark"></span>
                  <span className="label-text">⭐ Tin tuyển dụng nổi bật</span>
                </label>
                <small>Tin nổi bật sẽ được hiển thị ưu tiên trên trang chủ</small>
              </div>
            </div>

            {/* Preview */}
            <div className="form-section">
              <h3>👀 Xem trước</h3>
              <div className="job-preview">
                <div className="preview-title">
                  {formData.title || 'Tiêu đề vị trí'}
                </div>
                <div className="preview-info">
                  <span className="preview-department">
                    {departments.find(d => d.value === formData.department)?.label}
                  </span>
                  <span className="preview-type">
                    {jobTypes.find(t => t.value === formData.job_type)?.label}
                  </span>
                  <span className="preview-location">
                    📍 {formData.location}
                  </span>
                </div>
                <div className="preview-summary">
                  {formData.summary || 'Mô tả ngắn về vị trí sẽ hiển thị ở đây...'}
                </div>
                {(formData.min_salary || formData.max_salary) && (
                  <div className="preview-salary">
                    💰 {formData.min_salary && formData.max_salary 
                      ? `${parseInt(formData.min_salary).toLocaleString()} - ${parseInt(formData.max_salary).toLocaleString()} VND`
                      : formData.min_salary 
                        ? `Từ ${parseInt(formData.min_salary).toLocaleString()} VND`
                        : `Lên đến ${parseInt(formData.max_salary).toLocaleString()} VND`
                    }
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="form-actions">
          <button 
            type="button" 
            onClick={() => navigate('/admin/jobs')}
            className="cancel-btn"
            disabled={loading}
          >
            Hủy bỏ
          </button>
          
          <button 
            type="submit" 
            disabled={loading || uploading}
            className="submit-btn"
          >
            {uploading ? '📤 Đang upload hình ảnh...' : loading ? '💾 Đang cập nhật...' : '💾 Cập nhật tin tuyển dụng'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdminJobEdit; 